import type { CardFieldSlot, CardRow, PipelineCardConfig } from './cardConfig';
import type { CardFieldRef } from './cardConfigOptions';

export const MAX_SLOTS = 2;

const EMPTY_SLOT: CardFieldSlot = { type: 'property', name: '' };

function arrayMove<T>(array: T[], from: number, to: number): T[] {
  const result = [...array];
  const [item] = result.splice(from, 1);
  result.splice(to, 0, item);
  return result;
}

function withRows(
  config: PipelineCardConfig,
  rows: CardRow[]
): PipelineCardConfig {
  return { ...config, rows };
}

function withRowAt(
  config: PipelineCardConfig,
  rowIndex: number,
  row: CardRow
): PipelineCardConfig {
  if (rowIndex < 0 || rowIndex >= config.rows.length) {
    return config;
  }
  const rows = config.rows.map((r, i) => (i === rowIndex ? row : r));
  return withRows(config, rows);
}

function withSlotAt(
  config: PipelineCardConfig,
  rowIndex: number,
  slotIndex: number,
  slot: CardFieldSlot
): PipelineCardConfig {
  if (rowIndex < 0 || rowIndex >= config.rows.length) {
    return config;
  }
  const row = config.rows[rowIndex];
  if (slotIndex < 0 || slotIndex >= row.slots.length) {
    return config;
  }
  const slots = row.slots.map((s, i) => (i === slotIndex ? slot : s));
  return withRowAt(config, rowIndex, { ...row, slots });
}

export function addRow(config: PipelineCardConfig): PipelineCardConfig {
  return withRows(config, [...config.rows, { slots: [{ ...EMPTY_SLOT }] }]);
}

export function removeRow(
  config: PipelineCardConfig,
  rowIndex: number
): PipelineCardConfig {
  if (rowIndex < 0 || rowIndex >= config.rows.length) {
    return config;
  }
  return withRows(
    config,
    config.rows.filter((_, i) => i !== rowIndex)
  );
}

export function moveRow(
  config: PipelineCardConfig,
  fromIndex: number,
  toIndex: number
): PipelineCardConfig {
  if (
    fromIndex < 0 ||
    fromIndex >= config.rows.length ||
    toIndex < 0 ||
    toIndex >= config.rows.length
  ) {
    return config;
  }
  return withRows(config, arrayMove(config.rows, fromIndex, toIndex));
}

export function addSlot(
  config: PipelineCardConfig,
  rowIndex: number
): PipelineCardConfig {
  if (rowIndex < 0 || rowIndex >= config.rows.length) {
    return config;
  }
  const row = config.rows[rowIndex];
  if (row.slots.length >= MAX_SLOTS) {
    return config;
  }
  return withRowAt(config, rowIndex, {
    ...row,
    slots: [...row.slots, { ...EMPTY_SLOT }],
  });
}

export function removeSlot(
  config: PipelineCardConfig,
  rowIndex: number,
  slotIndex: number
): PipelineCardConfig {
  if (rowIndex < 0 || rowIndex >= config.rows.length) {
    return config;
  }
  const row = config.rows[rowIndex];
  if (slotIndex < 0 || slotIndex >= row.slots.length) {
    return config;
  }
  const slots = row.slots.filter((_, i) => i !== slotIndex);
  if (slots.length === 0) {
    return removeRow(config, rowIndex);
  }
  return withRowAt(config, rowIndex, { ...row, slots });
}

export function setSlotField(
  config: PipelineCardConfig,
  rowIndex: number,
  slotIndex: number,
  field: CardFieldRef
): PipelineCardConfig {
  if (rowIndex < 0 || rowIndex >= config.rows.length) {
    return config;
  }
  const row = config.rows[rowIndex];
  if (slotIndex < 0 || slotIndex >= row.slots.length) {
    return config;
  }
  const existing = row.slots[slotIndex];
  const updated: CardFieldSlot = {
    ...existing,
    type: field.type,
    name: field.name,
  };
  return withSlotAt(config, rowIndex, slotIndex, updated);
}

export function addFallback(
  config: PipelineCardConfig,
  rowIndex: number,
  slotIndex: number
): PipelineCardConfig {
  if (rowIndex < 0 || rowIndex >= config.rows.length) {
    return config;
  }
  const row = config.rows[rowIndex];
  if (slotIndex < 0 || slotIndex >= row.slots.length) {
    return config;
  }
  const slot = row.slots[slotIndex];
  const fallbacks = [...(slot.fallbacks ?? []), { ...EMPTY_SLOT }];
  return withSlotAt(config, rowIndex, slotIndex, { ...slot, fallbacks });
}

export function removeFallback(
  config: PipelineCardConfig,
  rowIndex: number,
  slotIndex: number,
  fallbackIndex: number
): PipelineCardConfig {
  if (rowIndex < 0 || rowIndex >= config.rows.length) {
    return config;
  }
  const row = config.rows[rowIndex];
  if (slotIndex < 0 || slotIndex >= row.slots.length) {
    return config;
  }
  const slot = row.slots[slotIndex];
  if (
    !slot.fallbacks ||
    fallbackIndex < 0 ||
    fallbackIndex >= slot.fallbacks.length
  ) {
    return config;
  }
  const remaining = slot.fallbacks.filter((_, i) => i !== fallbackIndex);
  const updated: CardFieldSlot = {
    ...slot,
    fallbacks: remaining.length > 0 ? remaining : undefined,
  };
  return withSlotAt(config, rowIndex, slotIndex, updated);
}

export function toggleSlotLabel(
  config: PipelineCardConfig,
  rowIndex: number,
  slotIndex: number
): PipelineCardConfig {
  if (rowIndex < 0 || rowIndex >= config.rows.length) {
    return config;
  }
  const row = config.rows[rowIndex];
  if (slotIndex < 0 || slotIndex >= row.slots.length) {
    return config;
  }
  const slot = row.slots[slotIndex];
  const updated: CardFieldSlot = {
    ...slot,
    showLabel: !slot.showLabel,
  };
  return withSlotAt(config, rowIndex, slotIndex, updated);
}

export function setFallbackField(
  config: PipelineCardConfig,
  rowIndex: number,
  slotIndex: number,
  fallbackIndex: number,
  field: CardFieldRef
): PipelineCardConfig {
  if (rowIndex < 0 || rowIndex >= config.rows.length) {
    return config;
  }
  const row = config.rows[rowIndex];
  if (slotIndex < 0 || slotIndex >= row.slots.length) {
    return config;
  }
  const slot = row.slots[slotIndex];
  if (
    !slot.fallbacks ||
    fallbackIndex < 0 ||
    fallbackIndex >= slot.fallbacks.length
  ) {
    return config;
  }
  const fallbacks = slot.fallbacks.map((f, i) =>
    i === fallbackIndex ? { ...f, type: field.type, name: field.name } : f
  );
  return withSlotAt(config, rowIndex, slotIndex, { ...slot, fallbacks });
}
